import { LitElement, html, css } from 'lit';
import { router } from './lib/router.js';
import { applySiteMeta, fetchDraftNavPages, fetchPages, fetchSiteSettings } from './lib/api.js';
import { store } from './lib/store.js';
import { isDraftPath } from './lib/build-paths.js';
const PAGE_PATHS = __SITE_CONFIG__.routes || {};

// Apply the persisted color theme before first render (no flash).
const savedTheme = localStorage.getItem('site-theme');
if (savedTheme === 'dark' || savedTheme === 'light') {
  document.documentElement.dataset.theme = savedTheme;
}

import './components/site-header.js';
import './components/site-footer.js';
import './components/site-draft-panel.js';
import './pages/site-cms-page.js';
import './pages/site-search-page.js';

// Register routes
const SEARCH_ROUTE = typeof __SITE_CONFIG__.features?.search === 'object'
  ? (__SITE_CONFIG__.features.search.route || 'search')
  : (__SITE_CONFIG__.features?.search ? 'search' : null);
for (const [slug, paths] of Object.entries(PAGE_PATHS)) {
  router.route(slug, slug === SEARCH_ROUTE ? 'site-search-page' : 'site-cms-page', paths);
}

class SiteApp extends LitElement {
  static properties = {
    _currentSlug: { state: true },
    _currentComponent: { state: true },
    _isDraft: { state: true },
    _navLoaded: { state: true },
    _draftPageTitle: { state: true },
    _draftPageId: { state: true },
    _draftPageChanged: { state: true },
    _draftPageReady: { state: true },
    _draftSettingsChanged: { state: true },
    _customTemplate: { state: true },
  };

  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      min-height: 100vh;
    }
    .main {
      flex: 1;
      padding-top: var(--header-height, 4rem);
    }
    .main--flush {
      padding-top: 0;
    }
    .loading {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 60vh;
      flex-direction: column;
      gap: var(--space-4, 1rem);
    }
    .spinner {
      width: 40px;
      height: 40px;
      border: 3px solid var(--color-border, rgba(255,255,255,0.08));
      border-top-color: var(--color-accent, #00d4ff);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    .loading-text {
      font-size: var(--text-sm, 0.875rem);
      color: var(--color-text-muted, #6b6b80);
    }
  `;

  constructor() {
    super();
    this._currentSlug = null;
    this._currentComponent = null;
    this._isDraft = false;
    this._navLoaded = false;
    this._draftPageTitle = 'Home';
    this._draftPageId = null;
    this._draftPageChanged = false;
    this._draftPageReady = false;
    this._draftSettingsChanged = false;
    this._customTemplate = false;

    router.addEventListener('route-changed', (e) => {
      this._currentSlug = e.detail.slug;
      this._currentComponent = e.detail.component;
      this._isDraft = e.detail.isDraft;
      this._draftPageTitle = e.detail.slug || 'home';
      this._draftPageChanged = false;
      this._draftPageReady = false;
      this._customTemplate = false;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  _handleTemplateChanged(event) {
    this._customTemplate = event.detail.template !== 'built-in';
  }

  async connectedCallback() {
    super.connectedCallback();
    const draftMode = isDraftPath();
    try {
      const [pages, settings] = await Promise.all([
        draftMode ? fetchDraftNavPages() : fetchPages(),
        fetchSiteSettings(),
      ]);
      for (const page of pages) {
        router.route(page.slug, page.slug === SEARCH_ROUTE ? 'site-search-page' : 'site-cms-page', PAGE_PATHS[page.slug] || [`/${page.slug}/`]);
      }
      store.setNavPages(pages);
      store.setSiteSettings(settings);
      this._draftSettingsChanged = Boolean(settings?.has_unpublished_changes);
    } catch {
      const fallbackPages = (__SITE_FALLBACK__.pages || []).map(({ content, ...page }) => page);
      store.setNavPages(fallbackPages);
      store.setSiteSettings(__SITE_SETTINGS_FALLBACK__);
      applySiteMeta(__SITE_SETTINGS_FALLBACK__);
    }
    this._navLoaded = true;
    router.refresh();
  }

  _handleDraftPageLoaded(event) {
    const detail = event.detail || {};
    if (detail.slug !== (this._currentSlug || 'home')) return;
    this._draftPageTitle = detail.title || detail.slug || 'Page';
    this._draftPageId = detail.id || null;
    this._draftPageChanged = Boolean(detail.hasUnpublishedChanges);
    this._draftPageReady = Boolean(detail.ready);
  }

  _handleDraftPublished(event) {
    if (event.detail?.target === 'site') this._draftSettingsChanged = false;
    if (event.detail?.target === 'page') this._draftPageChanged = false;
  }

  render() {
    if (!this._navLoaded) {
      return html`
        <site-header></site-header>
        <div class="main ${this._currentSlug === 'signup' || this._currentSlug === 'home' ? 'main--flush' : ''}">
          <div class="loading">
            <div class="spinner"></div>
            <div class="loading-text">${store.getSiteSettings()?.interface?.loading_text || 'Loading'}</div>
          </div>
        </div>
        <site-footer></site-footer>
      `;
    }

    const useCustom = this._customTemplate;
    const useSearch = this._currentComponent === 'site-search-page';
    const showDraftPanel = this._isDraft && !useSearch && window.parent === window;
    return html`
      ${useCustom ? '' : html`<site-header></site-header>`}
      <div class="main ${!useCustom && !useSearch && (this._currentSlug === 'signup' || this._currentSlug === 'home') ? 'main--flush' : ''}">
        ${useSearch ? html`<site-search-page></site-search-page>` : html`
          <site-cms-page
            .slug=${this._currentSlug || 'home'}
            .pageId=${this._draftPageId}
            .draftMode=${this._isDraft}
            @draft-page-loaded=${this._handleDraftPageLoaded}
            @template-changed=${this._handleTemplateChanged}
          ></site-cms-page>
        `}
      </div>
      ${useCustom ? '' : html`<site-footer></site-footer>`}
      ${showDraftPanel ? html`
        <site-draft-panel
          .slug=${this._currentSlug || 'home'}
          .pageId=${this._draftPageId}
          .pageTitle=${this._draftPageTitle}
          .pageChanged=${this._draftPageChanged}
          .pageReady=${this._draftPageReady}
          .settingsChanged=${this._draftSettingsChanged}
          @draft-published=${this._handleDraftPublished}
        ></site-draft-panel>
      ` : ''}
    `;
  }
}

customElements.define('site-app', SiteApp);
